import { Text } from '@mantine/core';
import { CellSort, EmptyRowsFallback } from '@shared/ui';
import { StatusBage } from '@shared/ui/DataTable/StatusBage';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { DataTable } from '@shared/ui/DataTable';
import type { User, Users } from '@entities/user';
import { type SortQuery } from '@shared/types';
import { formatPhoneNumber, joinStringWithFallback } from '@shared/helpers';

type UserListProps = {
  users: Users;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  recordsPerPageOptions?: number[];
  onRecordsPerPageChange?: (pageSize: number) => void;
  setPageSize: (pageSize: number) => void;
  isFilledFilters: boolean;
  onSortChange: (field: string, direction?: 'asc' | 'desc') => void;
  currentSort: SortQuery;
  isLoading: boolean;
  totalRecords?: number;
};

export const UserList = ({
  users,
  page,
  setPage,
  pageSize,
  recordsPerPageOptions,
  onRecordsPerPageChange,
  setPageSize,
  totalRecords,
  isFilledFilters,
  onSortChange,
  currentSort,
  isLoading,
}: UserListProps) => {
  const navigate = useNavigate();

  const columns = useMemo(
    () => [
      {
        accessor: 'id',
        title: (
          <CellSort
            label='ID'
            onSort={dir => onSortChange('id', dir)}
            currentDirection={currentSort.id}
          />
        ),
      },
      {
        accessor: 'name',
        title: (
          <CellSort
            label='Фамилия Имя'
            onSort={dir => onSortChange('profile.fullName', dir)}
            currentDirection={currentSort['profile.fullName']}
          />
        ),
        render: ({ profile }: User) => (
          <Text>
            {joinStringWithFallback([profile?.firstName, profile?.lastName], {
              fallback: '-',
            })}
          </Text>
        ),
      },
      {
        accessor: 'email',
        title: (
          <CellSort
            label='Email'
            onSort={dir => onSortChange('email', dir)}
            currentDirection={currentSort.email}
          />
        ),
      },
      {
        accessor: 'profile.phone',
        title: (
          <CellSort
            label='Номер телефона'
            onSort={dir => onSortChange('profile.phone', dir)}
            currentDirection={currentSort['profile.phone']}
          />
        ),
        render: ({ profile }: User) => (
          <Text>{formatPhoneNumber(profile?.phone || '')}</Text>
        ),
      },
      {
        accessor: 'role.displayName',
        title: (
          <CellSort
            label='Группа'
            onSort={dir => onSortChange('role.name', dir)}
            currentDirection={currentSort['role.name']}
          />
        ),
      },
      {
        accessor: 'company.name',
        title: (
          <CellSort
            label='Компания'
            onSort={dir => onSortChange('company.name', dir)}
            currentDirection={currentSort['company.name']}
          />
        ),
      },
      {
        accessor: 'isActive',
        title: (
          <CellSort
            label='Статус'
            onSort={dir => onSortChange('isActive', dir)}
            currentDirection={currentSort.isActive}
          />
        ),
        render: ({ isActive }: User) => <StatusBage isActive={isActive} />,
      },
    ],
    [currentSort, onSortChange]
  );

  const handleRowClick = ({ record }: { record: User }) => {
    navigate(`/user-list/${record.id}`);
  };

  const emptyState = useMemo(() => {
    if (isFilledFilters) {
      return {
        desc: 'К сожалению, совпадений не найдено',
        subDesc:
          'Попробуйте изменить параметры фильтров. Чтобы вернуться ко всему списку, сбросьте фильтр.',
      };
    }

    return {
      desc: 'Список пользователей пуст',
      subDesc:
        'Чтобы создать пользователя, нажмите кнопку «Создать пользователя»',
    };
  }, [isFilledFilters]);

  return (
    <DataTable<User>
      fetching={isLoading}
      columns={columns}
      records={users}
      onRowClick={handleRowClick}
      page={page}
      setPage={setPage}
      recordsPerPageOptions={recordsPerPageOptions}
      onRecordsPerPageChange={onRecordsPerPageChange}
      pageSize={pageSize}
      setPageSize={setPageSize}
      recordsPerPage={pageSize}
      onPageChange={setPage}
      totalRecords={totalRecords}
      emptyState={
        <EmptyRowsFallback
          desc={emptyState.desc}
          subDesc={emptyState.subDesc}
        />
      }
      highlightOnHover
    />
  );
};
